import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import { ButtonOBSOLETED, Notification, Message } from '../../web.common';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import { Filters, FilterInput, FilterSelect, FilterSelectAsync } from './Filters';
import { getQueryStringParameters } from '../../common/utils';
import { DisplayGuid, DisplayBoolean, DisplayType } from '../items';

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'blacklisted', label: 'Blacklisted' },
];

const navigationButtonStyle = {
  width: '6%',
  color: 'grey',
  fontSize: '0.85rem',
};

const SORTABLE_COLUMNS = ['name', 'blacklistreason'];

const NonPartnerFacilitiesInternal = (props) => {
  const [loadedData, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [reasonOptions, setReasonOptions] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const filters = getQueryStringParameters(props.location.search);
        const data = await props.api.nonPartnerFacilities.searchNonPartnerFacilities({ filters });
        setData(data);
      } catch (e) {
        setNotification(new Message('error', 'Unable to load data'));
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [props.location.search]);

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const reasons = await props.api.nonPartnerFacilities.lookupReasons('');
        setReasonOptions(reasons);
      } catch (e) {
        // non bloccante
      }
    };
    fetchReasons();
  }, []);

  const urlFilters = getQueryStringParameters(props.location.search);
  const page = parseInt(urlFilters.page) || 1;
  const orderBy = urlFilters.orderBy || 'name';
  const orderDirection = urlFilters.orderDirection || 'asc';

  const orderHandler = (columnKey) => {
    const { history, location } = props;
    const newDirection = columnKey === orderBy
      ? (orderDirection === 'asc' ? 'desc' : 'asc')
      : 'asc';
    Filters.goToPage(history, location, 1, columnKey, newDirection);
  };

  const pagingHandler = (action) => {
    const { history, location } = props;
    if (action === 'add' && page < loadedData.pages) {
      Filters.goToPage(history, location, page + 1, orderBy, orderDirection);
    }
    if (action === 'sub' && page > 1) {
      Filters.goToPage(history, location, page - 1, orderBy, orderDirection);
    }
  };

  const sortIndicator = (columnKey) => {
    if (columnKey !== orderBy) return '';
    return orderDirection === 'desc' ? ' ↓' : ' ↑';
  };

  const header = [
    {
      key: 'id',
      label: 'Id',
      render: ({ id }) => <DisplayGuid baseAnchor='non-partner-facility'>{id}</DisplayGuid>,
    },
    {
      key: 'name',
      label: 'Name',
      render: ({ name }) => <span>{name}</span>,
    },
    {
      key: 'slug',
      label: 'Slug',
      render: ({ slug }) => <span>{slug}</span>,
    },
    {
      key: 'area',
      label: 'Area',
      render: ({ area }) => <span>{area || '-'}</span>,
    },
    {
      key: 'isDeleted',
      label: 'Deleted',
      render: ({ isDeleted }) => <DisplayBoolean>{isDeleted}</DisplayBoolean>,
    },
    {
      key: 'isBlacklisted',
      label: 'Blacklisted',
      render: ({ isBlacklisted }) => <DisplayBoolean>{isBlacklisted}</DisplayBoolean>,
    },
    {
      key: 'blacklistreason',
      label: 'Blacklist Reason',
      render: ({ blacklistReason }) => <span>{blacklistReason || '-'}</span>,
    },
    {
      key: 'sourceType',
      label: 'Source Type',
      render: ({ sourceType }) => sourceType ? <DisplayType>{sourceType}</DisplayType> : <span>-</span>,
    },
  ];

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className='content-with-right-pane-delimiter'>
            <PageTop
              documentTitle='Non Partner Facilities'
              documentDescription='Non Partner Facilities list'
            />
            <h2 className='main-page-header'>Non Partner Facilities</h2>
            <Filters
              initFromUrl
              submitUrl
              validateForm={({ id, name, slug, area, sourceType, status, reason, resultsPerPage }, updatedKey) => {
                if (updatedKey !== 'id') {
                  return { id, name, slug, area, sourceType, status, reason, resultsPerPage };
                }
                return { id };
              }}
              render={(formData, update) => (
                <div className='filters-block'>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='id'>Id</label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name='id'
                      placeholder='Find by id'
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='name'>Name</label>
                    <FilterSelectAsync
                      className='filter-select'
                      data={formData}
                      debounce
                      update={update}
                      name='name'
                      loadOptions={async inputValue => props.api.nonPartnerFacilities.lookupByName(inputValue)}
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='slug'>Slug</label>
                    <FilterSelectAsync
                      className='filter-select'
                      data={formData}
                      debounce
                      update={update}
                      name='slug'
                      loadOptions={async inputValue => props.api.nonPartnerFacilities.lookupBySlug(inputValue)}
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='area'>Area</label>
                    <FilterSelectAsync
                      className='filter-select'
                      data={formData}
                      debounce
                      update={update}
                      name='area'
                      loadOptions={async inputValue => props.api.nonPartnerFacilities.lookupByArea(inputValue)}
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='sourceType'>Source Type</label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name='sourceType'
                      placeholder='Filter by source type'
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='reason'>Blacklist Reason</label>
                    <FilterSelect
                      className='filter-select'
                      data={formData}
                      update={update}
                      name='reason'
                      options={reasonOptions}
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='resultsPerPage'>Results Per Page</label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name='resultsPerPage'
                      placeholder='Default: 20'
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='status'>Status</label>
                    <FilterSelect
                      className='filter-select'
                      data={formData}
                      update={update}
                      name='status'
                      options={statusOptions}
                    />
                  </div>
                  <br />
                  <br />
                  <div className='filters-submit-wrapper'>
                    <ButtonOBSOLETED
                      type='submit'
                      buttonContent='Search'
                      disabled={isLoading}
                    />
                  </div>
                  <hr />
                </div>
              )}
            />
            {loadedData == null ? (
              <span>Loading...</span>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <input
                    style={navigationButtonStyle}
                    type='button'
                    value='< Prev'
                    onClick={() => pagingHandler('sub')}
                  />
                  <span style={{ color: 'grey', fontSize: '0.85rem' }}>
                    Showing page {page} out of {loadedData.pages} ({loadedData.totalCount} total)
                  </span>
                  <input
                    style={navigationButtonStyle}
                    type='button'
                    value='Next >'
                    onClick={() => pagingHandler('add')}
                  />
                </div>
                <br />
                <article className='data-table-wrap'>
                  <table className='data-lists component-data-table cell-content-align-top'>
                    <thead>
                      <tr>
                        {header.map((col) => {
                          const sortable = SORTABLE_COLUMNS.includes(col.key);
                          return (
                            <td
                              key={col.key}
                              style={sortable ? { cursor: 'pointer' } : {}}
                              onClick={sortable ? () => orderHandler(col.key) : undefined}
                            >
                              {col.label}{sortIndicator(col.key)}
                            </td>
                          );
                        })}
                      </tr>
                    </thead>
                    {loadedData.facilities.map((row) => (
                      <tbody key={row.id}>
                        <tr className='hover-listview'>
                          {header.map((col) => (
                            <td key={col.key}>
                              <strong className='table-cell-label'>{col.label}</strong>
                              {col.render(row)}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    ))}
                  </table>
                </article>
              </>
            )}
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const NonPartnerFacilities = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(NonPartnerFacilitiesInternal)
  )
);

NonPartnerFacilities.requiresLogin = true;
NonPartnerFacilities.propTypes = { isLoggedIn: PropTypes.bool };

export default NonPartnerFacilities;
